import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { MapPin, Phone, Mail, Facebook, Instagram, Twitter, Linkedin } from 'lucide-react'
import { api } from '../lib/api'
import { getCacheStale, setCache } from '../lib/cache'

const Footer = () => {
  const currentYear = new Date().getFullYear()
  const [settings, setSettings] = useState<any>({
    contact_email: 'colmafacil@gmail.com',
    contact_phone: '+1 (829) 441-9998',
    contact_address: 'República Dominicana',
    social_facebook: 'https://facebook.com/colmafacil',
    social_instagram: 'https://instagram.com/colmafacil',
    social_twitter: 'https://twitter.com/colmafacil',
    business_name: 'ColmaFácil',
    business_description: 'Tu marketplace de confianza para colmados en República Dominicana.'
  })

  // Ref para prevenir ejecuciones duplicadas (React StrictMode)
  const loadingSettingsRef = useRef(false)

  useEffect(() => {
    // Prevenir ejecuciones duplicadas causadas por React StrictMode
    if (loadingSettingsRef.current) return
    loadingSettingsRef.current = true

    loadSettings().finally(() => {
      loadingSettingsRef.current = false
    })
  }, [])

  const loadSettings = async () => {
    const cacheKey = 'platform_settings_public'
    
    // Intentar cargar desde cache primero (evitar query HTTP innecesaria)
    const cachedSettings = getCacheStale<any>(cacheKey)
    if (cachedSettings) {
      setSettings(cachedSettings)
      // Si tenemos cache válido, no hacer query HTTP (evitar 304 innecesario)
      return
    }

    try {
      const response = await api.getPublicPlatformSettings()
      if (response.success && response.data?.flat) {
        setSettings(response.data.flat)
        // Guardar en cache (30 minutos de TTL - settings cambian poco)
        setCache(cacheKey, response.data.flat, 30 * 60 * 1000)
      }
    } catch (error) {
      console.error('Error loading platform settings:', error)
      // Use defaults if API fails
    }
  }

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {/* Brand Section */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-1">
            <div className="mb-4">
              <img
                src="/image/ColmaFacil-Logo.png"
                alt="ColmaFácil"
                className="h-10 sm:h-12 w-auto mb-4"
              />
            </div>
            <p className="text-sm sm:text-base text-gray-300 mb-4 leading-relaxed">
              {settings.business_description || 'Tu marketplace de confianza para colmados en República Dominicana. Productos frescos, entrega rápida y precios justos.'}
            </p>
            <div className="flex space-x-4">
              {settings.social_facebook && (
                <a
                  href={settings.social_facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-white/10 hover:bg-primary-600 flex items-center justify-center transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {settings.social_instagram && (
                <a
                  href={settings.social_instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-white/10 hover:bg-primary-600 flex items-center justify-center transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {settings.social_twitter && (
                <a
                  href={settings.social_twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-white/10 hover:bg-primary-600 flex items-center justify-center transition-colors"
                  aria-label="Twitter"
                >
                  <Twitter className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-base sm:text-lg font-bold mb-4">Enlaces Rápidos</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm sm:text-base text-gray-300 hover:text-primary-400 transition-colors">
                  Inicio
                </Link>
              </li>
              <li>
                <Link to="/stores" className="text-sm sm:text-base text-gray-300 hover:text-primary-400 transition-colors">
                  Negocios
                </Link>
              </li>
              <li>
                <Link to="/news" className="text-sm sm:text-base text-gray-300 hover:text-primary-400 transition-colors">
                  Noticias
                </Link>
              </li>
              <li>
                <Link to="/register" className="text-sm sm:text-base text-gray-300 hover:text-primary-400 transition-colors">
                  Únete como Negocio
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-base sm:text-lg font-bold mb-4">Soporte</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/help" className="text-sm sm:text-base text-gray-300 hover:text-primary-400 transition-colors">
                  Centro de Ayuda
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm sm:text-base text-gray-300 hover:text-primary-400 transition-colors">
                  Contáctanos
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-sm sm:text-base text-gray-300 hover:text-primary-400 transition-colors">
                  Política de Privacidad
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-sm sm:text-base text-gray-300 hover:text-primary-400 transition-colors">
                  Términos y Condiciones
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-base sm:text-lg font-bold mb-4">Contacto</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm sm:text-base text-gray-300">
                  {settings.contact_address || 'República Dominicana'}
                </span>
              </li>
              {settings.contact_phone && (
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-primary-400 flex-shrink-0" />
                  <a href={`tel:${settings.contact_phone.replace(/\s/g, '')}`} className="text-sm sm:text-base text-gray-300 hover:text-primary-400 transition-colors">
                    {settings.contact_phone}
                  </a>
                </li>
              )}
              {settings.contact_email && (
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-primary-400 flex-shrink-0" />
                  <a href={`mailto:${settings.contact_email}`} className="text-sm sm:text-base text-gray-300 hover:text-primary-400 transition-colors break-all">
                    {settings.contact_email}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-gray-700">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs sm:text-sm text-gray-400 text-center sm:text-left">
              © {currentYear} {settings.business_name || 'ColmaFácil'}. Todos los derechos reservados.
            </p>
            <div className="flex items-center gap-4 text-xs sm:text-sm text-gray-400">
              <span>Hecho en República Dominicana</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer

